// =============================================================================
// PEDRA — src/optimizer/maxrects.ts
// MaxRects (retangulos maximais) para o modo de CORTE LIVRE — router, CNC ou
// serra de fita, onde o corte nao precisa ser passante.
//
// O resultado so entra no plano final se `reconstructGuillotine` conseguir
// descrever o layout como uma sequencia de cortes passantes: o CutList
// Optimizer original e 100% guilhotinado e este clone tambem e. Quando a
// reconstrucao falha, a tentativa e descartada — mas quando ela funciona
// (acontece com frequencia), sai um aproveitamento melhor de graca.
//
// KERF POR INFLACAO
// -----------------------------------------------------------------------------
// O bin roda com `largura + kerf` x `comprimento + kerf` e cada peca ocupa
// `largura + kerf` x `comprimento + kerf`. Duas pecas inflacionadas encostadas
// deixam as pecas REAIS exatamente a um kerf de distancia; uma peca encostada
// na borda termina exatamente na borda real. Matematicamente equivalente a
// descontar a lamina em cada corte.
// =============================================================================

import type { Micron, Micron2, Placement, Rect, Settings, StockSheet } from '../core/types';
import type { FitRule, PackItem } from './guillotine';
import { makePlacement, orientationsFor } from './guillotine';
import type { OrientationOption } from './guillotine';
import { containsRect, makeRect } from './geometry';

/** Teto de retangulos livres mantidos; acima disso a heuristica degrada. */
const MAX_FREE_RECTS = 4_000;

export interface MaxRectsState {
  /** Dimensoes JA INFLADAS do bin. */
  binWidth: Micron;
  binLength: Micron;
  kerf: Micron;
  /** Retangulos MAXIMAIS livres (inflados). Podem se sobrepor. */
  free: Rect[];
  /** Retangulos ocupados (inflados). */
  used: Rect[];
}

export interface MaxRectsResult {
  placements: Placement[];
  freeRects: Rect[];
  usedArea: Micron2;
  leftover: PackItem[];
}

interface Position {
  rect: Rect;
  rotated: boolean;
  score1: number;
  score2: number;
}

/** Cria o estado inicial com o bin ja inflado pelo kerf. */
export function newMaxRectsState(sheet: StockSheet, kerf: Micron): MaxRectsState {
  const k = Number.isFinite(kerf) && kerf > 0 ? kerf : 0;
  const binWidth = (sheet.width > 0 ? sheet.width : 0) + k;
  const binLength = (sheet.length > 0 ? sheet.length : 0) + k;
  return {
    binWidth,
    binLength,
    kerf: k,
    free: [makeRect(0, 0, binWidth, binLength)],
    used: [],
  };
}

// -----------------------------------------------------------------------------
// ESCOLHA DA POSICAO
// -----------------------------------------------------------------------------

function scoreFor(fr: Rect, w: Micron, l: Micron, rule: FitRule, st: MaxRectsState): [number, number] {
  const dw = fr.width - w;
  const dl = fr.length - l;
  const short = dw < dl ? dw : dl;
  const long = dw > dl ? dw : dl;
  switch (rule) {
    case 'bestArea':
      return [fr.width * fr.length - w * l, short];
    case 'bestShortSide':
      return [short, long];
    case 'bestLongSide':
      return [long, short];
    case 'bottomLeft':
      return [fr.y + l, fr.x];
    case 'exactFit':
      return [
        (dw === 0 ? 0 : 1) + (dl === 0 ? 0 : 1) + contactPenalty(fr, w, l, st),
        fr.width * fr.length - w * l,
      ];
  }
}

/** Bonus (negativo) por encostar nas bordas do bin — favorece layouts compactos. */
function contactPenalty(fr: Rect, w: Micron, l: Micron, st: MaxRectsState): number {
  let touching = 0;
  if (fr.x === 0 || fr.x + w === st.binWidth) touching++;
  if (fr.y === 0 || fr.y + l === st.binLength) touching++;
  return -touching * 0.25;
}

/**
 * Melhor posicao para uma peca ja inflada, testando todas as orientacoes
 * permitidas. Desempate final deterministico por (score1, score2, y, x, giro).
 */
export function findPosition(
  st: MaxRectsState,
  orients: readonly OrientationOption[],
  rule: FitRule,
): Position | null {
  let best: Position | null = null;
  for (let i = 0; i < st.free.length; i++) {
    const fr = st.free[i] as Rect;
    for (let o = 0; o < orients.length; o++) {
      const or = orients[o] as OrientationOption;
      const w = or.width + st.kerf;
      const l = or.length + st.kerf;
      if (w > fr.width || l > fr.length) continue;
      const [s1, s2] = scoreFor(fr, w, l, rule, st);
      const cand: Position = {
        rect: makeRect(fr.x, fr.y, w, l),
        rotated: or.rotated,
        score1: s1,
        score2: s2,
      };
      if (best === null || betterPosition(cand, best)) best = cand;
    }
  }
  return best;
}

function betterPosition(a: Position, b: Position): boolean {
  if (a.score1 !== b.score1) return a.score1 < b.score1;
  if (a.score2 !== b.score2) return a.score2 < b.score2;
  if (a.rect.y !== b.rect.y) return a.rect.y < b.rect.y;
  if (a.rect.x !== b.rect.x) return a.rect.x < b.rect.x;
  return !a.rotated && b.rotated;
}

// -----------------------------------------------------------------------------
// DIVISAO DOS RETANGULOS LIVRES
// -----------------------------------------------------------------------------

/**
 * Divide `fr` pelos pedacos que sobram ao redor de `used`.
 * Devolve false quando nao ha interseccao (teste de eixo separador).
 */
export function splitFreeNode(fr: Rect, used: Rect, scratch: Rect[]): boolean {
  if (
    used.x >= fr.x + fr.width ||
    used.x + used.width <= fr.x ||
    used.y >= fr.y + fr.length ||
    used.y + used.length <= fr.y
  ) {
    return false;
  }

  const mark = scratch.length;

  if (used.x < fr.x + fr.width && used.x + used.width > fr.x) {
    // Faixa ACIMA da peca.
    if (used.y > fr.y && used.y < fr.y + fr.length) {
      pushFree(scratch, mark, makeRect(fr.x, fr.y, fr.width, used.y - fr.y));
    }
    // Faixa ABAIXO da peca.
    if (used.y + used.length < fr.y + fr.length) {
      const top = used.y + used.length;
      pushFree(scratch, mark, makeRect(fr.x, top, fr.width, fr.y + fr.length - top));
    }
  }

  if (used.y < fr.y + fr.length && used.y + used.length > fr.y) {
    // Faixa a ESQUERDA da peca.
    if (used.x > fr.x && used.x < fr.x + fr.width) {
      pushFree(scratch, mark, makeRect(fr.x, fr.y, used.x - fr.x, fr.length));
    }
    // Faixa a DIREITA da peca.
    if (used.x + used.width < fr.x + fr.width) {
      const left = used.x + used.width;
      pushFree(scratch, mark, makeRect(left, fr.y, fr.x + fr.width - left, fr.length));
    }
  }

  return true;
}

/** Insere no scratch mantendo apenas retangulos maximais entre os recem-criados. */
function pushFree(scratch: Rect[], mark: number, r: Rect): void {
  if (r.width <= 0 || r.length <= 0) return;
  for (let i = mark; i < scratch.length; ) {
    const other = scratch[i] as Rect;
    if (containsRect(other, r)) return;
    if (containsRect(r, other)) {
      scratch[i] = scratch[scratch.length - 1] as Rect;
      scratch.pop();
      continue;
    }
    i++;
  }
  scratch.push(r);
}

/** Remove da lista livre todo retangulo contido em outro (mantem so os maximais). */
export function pruneFreeList(st: MaxRectsState): void {
  const free = st.free;
  for (let i = 0; i < free.length; i++) {
    for (let j = i + 1; j < free.length; ) {
      const a = free[i] as Rect;
      const b = free[j] as Rect;
      if (containsRect(a, b)) {
        free[j] = free[free.length - 1] as Rect;
        free.pop();
        continue;
      }
      if (containsRect(b, a)) {
        free[i] = free[free.length - 1] as Rect;
        free.pop();
        i--;
        break;
      }
      j++;
    }
  }
}

/** Aplica um posicionamento: divide os livres afetados e reinsere os maximais. */
export function insertMaxRects(st: MaxRectsState, node: Rect): void {
  const scratch: Rect[] = [];
  for (let i = 0; i < st.free.length; ) {
    if (splitFreeNode(st.free[i] as Rect, node, scratch)) {
      st.free[i] = st.free[st.free.length - 1] as Rect;
      st.free.pop();
      continue;
    }
    i++;
  }
  for (let i = 0; i < scratch.length; i++) st.free.push(scratch[i] as Rect);
  pruneFreeList(st);
  if (st.free.length > MAX_FREE_RECTS) st.free.length = MAX_FREE_RECTS;
  st.used.push(node);
}

// -----------------------------------------------------------------------------
// EMPACOTAMENTO DE UMA CHAPA
// -----------------------------------------------------------------------------

/**
 * Empacota `items` (na ORDEM recebida) numa chapa usando MaxRects.
 * As dimensoes devolvidas em `placements` e `freeRects` sao REAIS
 * (ja descontada a inflacao do kerf).
 */
export function packSheetMaxRects(
  sheet: StockSheet,
  items: readonly PackItem[],
  settings: Settings,
  opts: { fit: FitRule },
): MaxRectsResult {
  const leftover: PackItem[] = [];
  const placements: Placement[] = [];
  let usedArea = 0;

  if (sheet.width <= 0 || sheet.length <= 0) {
    return { placements, freeRects: [], usedArea: 0, leftover: items.slice() };
  }

  const kerf = Number.isFinite(settings.kerf) && settings.kerf > 0 ? settings.kerf : 0;
  const st = newMaxRectsState(sheet, kerf);

  for (let i = 0; i < items.length; i++) {
    const item = items[i] as PackItem;
    if (item.width <= 0 || item.length <= 0) {
      leftover.push(item);
      continue;
    }
    const orients = orientationsFor(item, sheet, settings.considerGrain);
    const pos = findPosition(st, orients, opts.fit);
    if (pos === null) {
      leftover.push(item);
      continue;
    }
    const realWidth = pos.rect.width - kerf;
    const realLength = pos.rect.length - kerf;
    placements.push(
      makePlacement(item, pos.rect.x, pos.rect.y, realWidth, realLength, pos.rotated),
    );
    usedArea += realWidth * realLength;
    insertMaxRects(st, pos.rect);
  }

  return { placements, freeRects: realFreeRects(st, sheet), usedArea, leftover };
}

/** Converte os livres inflados em retangulos REAIS aproveitaveis. */
function realFreeRects(st: MaxRectsState, sheet: StockSheet): Rect[] {
  const out: Rect[] = [];
  for (let i = 0; i < st.free.length; i++) {
    const fr = st.free[i] as Rect;
    const w = Math.min(fr.width - st.kerf, sheet.width - fr.x);
    const l = Math.min(fr.length - st.kerf, sheet.length - fr.y);
    if (w <= 0 || l <= 0) continue;
    out.push(makeRect(fr.x, fr.y, w, l));
  }
  return out;
}
